import React from 'react';
import { Box, Popover, IconButton, styled } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarning } from '@fortawesome/pro-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useTheme, Theme } from '@mui/material';
import { Warnings } from './UnderwriterSupplementalInfo';
import { UW_ERROR_CLASS } from '../constants';

interface UnifiedMessagesButtonProps {
  warnings?: string[];
  errors?: string[];
  referralReasons?: string[]; // Business referrals
  referralReasonsForEdit?: string[]; // Edit referrals
}

type MessageBucket = {
  type: 'error' | 'warning' | 'editReferral' | 'businessReferral';
  title: string;
  messages: string[];
  color: string;
};

type BucketConfig = {
  type: MessageBucket['type'];
  title: string;
  getColor: (theme: Theme) => string;
  getMessages: (props: UnifiedMessagesButtonProps) => string[];
};

const StyledPopoverContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  minWidth: '250px',
  maxWidth: '400px',
}));

const StyledBucketContainer = styled(Box)(() => ({}));

const StyledBucketHeader = styled(Box)<{
  hasMessages: boolean;
  isFirst: boolean;
}>(({ theme, hasMessages, isFirst }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginBottom: hasMessages ? theme.spacing(0.5) : 0,
  marginTop: isFirst ? 0 : theme.spacing(1.5),
}));

const StyledBucketIconWrapper = styled(Box)<{ color: string }>(({ color }) => ({
  display: 'inline-flex',
  color,
  fontSize: '1rem',
}));

const StyledBucketTitle = styled(Box)<{ color: string }>(
  ({ color, theme }) => ({
    color,
    fontSize: '0.875rem',
    fontWeight: 600,
  })
);

const StyledMessageContainer = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(2.5),
  fontSize: '0.875rem',
  color: theme.palette.text.primary,
}));

const StyledMessageList = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(2.5),
  margin: 0,
  fontSize: '0.875rem',
  color: theme.palette.text.primary,
}));

const StyledMessageItem = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(0.25),
  paddingBottom: theme.spacing(0.25),
}));

const StyledButtonIconWrapper = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  marginRight: theme.spacing(0.15),
}));

const MESSAGE_CONFIGS: BucketConfig[] = [
  {
    type: 'error',
    title: Warnings.ERROR,
    getColor: (theme) => theme.palette.common.error,
    getMessages: ({ errors }) => errors || [],
  },
  {
    type: 'warning',
    title: Warnings.WARNING,
    getColor: (theme) => theme.palette.common.yellow[500],
    getMessages: ({ warnings }) => warnings || [],
  },
  {
    type: 'editReferral',
    title: Warnings.EDIT_REFERRAL,
    getColor: (theme) => theme.palette.common.purple,
    getMessages: ({ referralReasonsForEdit }) => referralReasonsForEdit || [],
  },
  {
    type: 'businessReferral',
    title: Warnings.REFERRAL,
    getColor: (theme) => theme.palette.common.cowbellBlue,
    getMessages: ({ referralReasons }) => referralReasons || [],
  },
];

const useMessageBuckets = (
  props: UnifiedMessagesButtonProps
): MessageBucket[] => {
  const theme = useTheme();
  return MESSAGE_CONFIGS.map((config) => {
    const messages = config.getMessages(props);
    return messages.length > 0
      ? {
          type: config.type,
          title: config.title,
          messages: Array.from(new Set(messages)),
          color: config.getColor(theme),
        }
      : null;
  }).filter((bucket): bucket is MessageBucket => bucket !== null);
};

const useButtonColor = (props: UnifiedMessagesButtonProps): string | null => {
  const theme = useTheme();
  for (const config of MESSAGE_CONFIGS) {
    const messages = config.getMessages(props);
    if (messages.length > 0) {
      return config.getColor(theme);
    }
  }
  return null;
};

const UnifiedMessagesButton = ({
  warnings = [],
  errors = [],
  referralReasons = [],
  referralReasonsForEdit = [],
}: UnifiedMessagesButtonProps) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const buckets = useMessageBuckets({
    errors,
    warnings,
    referralReasons,
    referralReasonsForEdit,
  });

  const buttonColor = useButtonColor({
    errors,
    warnings,
    referralReasons,
    referralReasonsForEdit,
  });

  // Don't render if no messages
  if (!buttonColor) {
    return null;
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        aria-label="View messages and warnings"
        aria-haspopup="true"
        className={errors.length > 0 ? UW_ERROR_CLASS : undefined}
        sx={{
          color: buttonColor,
          padding: '0.25rem',
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <StyledButtonIconWrapper>
          <FontAwesomeIcon icon={faWarning as IconProp} fontSize="1.25rem" />
        </StyledButtonIconWrapper>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <StyledPopoverContent>
          {buckets.map((bucket, bucketIndex) => (
            <StyledBucketContainer key={bucket.type}>
              {/* Bucket Header */}
              <StyledBucketHeader
                hasMessages={bucket.messages.length > 0}
                isFirst={bucketIndex === 0}
              >
                <StyledBucketIconWrapper color={bucket.color}>
                  <FontAwesomeIcon icon={faWarning as IconProp} />
                </StyledBucketIconWrapper>
                <StyledBucketTitle component="strong" color={bucket.color}>
                  {bucket.title}:
                </StyledBucketTitle>
              </StyledBucketHeader>

              {/* Messages */}
              {bucket.messages.length === 1 ? (
                // Single message - render inline without bullets
                <StyledMessageContainer>
                  {bucket.messages[0]}
                </StyledMessageContainer>
              ) : (
                // Multiple messages - render with bullets
                <StyledMessageList component="ul">
                  {bucket.messages.map((message) => (
                    <StyledMessageItem
                      key={`${bucket.type}-${message}`}
                      component="li"
                    >
                      {message}
                    </StyledMessageItem>
                  ))}
                </StyledMessageList>
              )}
            </StyledBucketContainer>
          ))}
        </StyledPopoverContent>
      </Popover>
    </>
  );
};

export default UnifiedMessagesButton;
